/**
 * The evolution curve of an ACRISS (CU-869fn0gcp phase 2, brief block 8, criterion 19): your average price and your
 * tier's at each pass that decided the ACRISS in the window, drawn by `CurveChart` with `xKind="pass"`. The rows are
 * `/last-changes/pushes?sipp=` (that ACRISS over every pick-up date of the sample; one row per pass × date).
 *
 * At each pass: **You** = the mean of your price after that pass (`own_after` as the server sends it: the target shelf
 * price when the decision counts — written, or simulated under Include — the price it read otherwise; v1.5) over the
 * pick-up dates the pass decided;
 * **Your tier** = the mean of the tier the pass compared with, over the dates that had one (≥ 2 competitors). A pass
 * that decided fewer than `FEW_DATES` dates is a hollow point. Means of the server's figures, in the page's unit —
 * nothing else is computed (TL C3). Pure and alias-free.
 */
import { inUnit } from './amounts.js'
import { fmtInstant } from './windowLabel.js'

export const FEW_DATES = 3
export const PASS_CURVE_COLORS = Object.freeze({ you: '#2563EB', tier: '#6B7280' })

const isNum = v => typeof v === 'number' && Number.isFinite(v)
const mean = xs => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
const r2 = v => (isNum(v) ? Math.round(v * 100) / 100 + 0 : null)

/**
 * The rows the curve can trust (QA phase 2): `/pushes` answers newest first and the read stops at a page limit, so
 * when fewer rows came than `total` the oldest pass read may be cut (its point would average only some of its dates)
 * and is dropped. `{ items, truncated, passes }` — `passes` = the passes kept.
 */
export function completePasses(items, total) {
	const rows = items || []
	const truncated = rows.length < (total || 0)
	let kept = rows
	if (truncated && rows.length) {
		const oldest = rows.reduce((a, b) => (String(b.ts || '') < String(a.ts || '') ? b : a)).pass
		kept = rows.filter(r => r.pass !== oldest)
	}
	return { items: kept, truncated, passes: new Set(kept.map(r => r.pass).filter(Boolean)).size }
}

/** The note under a cut curve, in the pushes table's words. */
export function truncatedNote(passes) {
	return `Showing the newest ${passes === 1 ? 'pass' : `${passes} passes`} of the window: choose a shorter window to see them all.`
}

/**
 * `{ main: [you, tier], others: [], xLabels, passes, empty }` — `main[].points` = `[{ d, value, thin }]` with
 * `d` = the pass index in time order; `passes[d]` = `{ pass, ts, dates, before, you, tier, tierDates }`.
 */
export function buildPassCurve(items, { unit = 'total', lor = 1, tz = null } = {}) {
	const byPass = new Map()
	for (const p of items || []) {
		if (!p?.pass) continue
		const g = byPass.get(p.pass) || { pass: p.pass, ts: p.ts, rows: [] }
		if (p.ts && (!g.ts || p.ts < g.ts)) g.ts = p.ts
		g.rows.push(p)
		byPass.set(p.pass, g)
	}
	const passes = [...byPass.values()].sort((a, b) => String(a.ts || '').localeCompare(String(b.ts || ''))).map(g => {
		const own = g.rows.map(p => inUnit(p.own_after, unit, lor)).filter(isNum)
		const before = g.rows.map(p => inUnit(p.own_before, unit, lor)).filter(isNum)
		const tier = g.rows.map(p => inUnit(p.market_after, unit, lor)).filter(isNum)
		return { pass: g.pass, ts: g.ts, dates: g.rows.length, before: r2(mean(before)), you: r2(mean(own)), tier: r2(mean(tier)), tierDates: tier.length }
	})
	const series = (key, name, role, pick, thin) => ({
		key, name, role, color: PASS_CURVE_COLORS[key], width: key === 'you' ? 2.75 : 2,
		points: passes.map((p, d) => ({ d, value: pick(p), thin: thin(p) })).filter(pt => isNum(pt.value)),
	})
	const main = [
		series('you', 'You', 'you', p => p.you, p => p.dates < FEW_DATES),
		series('tier', 'Your tier', 'median', p => p.tier, p => p.tierDates < FEW_DATES),
	]
	return {
		main, others: [], passes,
		xLabels: passes.map(p => fmtInstant(p.ts, tz)),
		empty: !main.some(s => s.points.length),
	}
}

/**
 * The tooltip (and the aria-live text) at pass `d`: `{ title, captured, rows }`, the shape `PriceCurve`'s `Tooltip` draws
 * (it shows whole euros, so the "before" here is whole euros too).
 */
export function passTooltip(curve, d) {
	const p = curve?.passes?.[d]
	if (!p) return null
	return {
		title: `Pass of ${curve.xLabels[d] || '—'}`,
		captured: `${p.dates} pick-up date${p.dates === 1 ? '' : 's'} decided${isNum(p.before) ? ` · you were at ${Math.round(p.before)} € before` : ''}`,
		rows: curve.main.map(s => {
			const pt = s.points.find(x => x.d === d)
			return { key: s.key, name: s.name, tag: null, color: s.color, value: pt ? pt.value : null, thin: !!pt?.thin, ly: null }
		}),
	}
}

/** What a screen reader hears for the whole curve: the two ends of each line. */
export function passCurveLabel(curve, sipp) {
	if (!curve || curve.empty) return `${sipp}: no pass with a price in the window.`
	const ends = curve.main.filter(s => s.points.length).map(s => {
		const first = s.points[0], last = s.points[s.points.length - 1]
		return `${s.name} from ${first.value.toFixed(2)} € to ${last.value.toFixed(2)} €`
	})
	return `${sipp}, average price by pass, ${curve.passes.length} pass${curve.passes.length === 1 ? '' : 'es'}: ${ends.join('; ')}. Use the left and right arrows to move pass by pass.`
}
